/**
 * Main App Component
 * Integrates Map and ChatInterface components
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import Map from './components/Map';
import ChatInterface from './components/ChatInterface';
import AnalyticsPanel from './components/AnalyticsPanel';
import DatabricksPanel from './components/DatabricksPanel';
import Layout from './components/Layout';
import AlertsRail from './components/AlertsRail';
import WorkspaceTabs from './components/WorkspaceTabs';
import SCADAPanel from './components/SCADAPanel';
import AuditPanel from './components/AuditPanel';
import OnboardingPanel from './components/OnboardingPanel';
import ExplainPanel from './components/ExplainPanel';
import MapToolbar from './components/MapToolbar';
import { getCurrentRole, canDo, useCurrentRole, isLayerVisibleForRole } from './components/RoleSwitcher';
import RoleToast from './components/RoleToast';
import AnnotationModal from './components/AnnotationModal';
import { logAudit } from './services/demoExtrasService';
import { getContractsByTractNumbers } from './services/contractService';
import { isWaterTenant, isEmissionsTenant, isNexusTenant } from './config/gisConfig';
import './styles/App.css';

const APP_TITLE = isEmissionsTenant
  ? 'Nexus Emissions'
  : isWaterTenant
  ? 'Nexus Water'
  : 'TPL GIS Query Assistant';

const APP_SUBTITLE = isEmissionsTenant
  ? 'AI-powered leak detection & emissions intelligence'
  : isWaterTenant
  ? 'AI-powered water asset intelligence'
  : 'Query your land and mineral data using natural language';

function App() {
  const [queryResults, setQueryResults] = useState(null);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [featureContracts, setFeatureContracts] = useState([]);
  const [error, setError] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [panelPosition, setPanelPosition] = useState({ x: 20, y: 150 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [databricksPanelOpen, setDatabricksPanelOpen] = useState(false);
  // SCADA tab data (mode: 'asset' | 'region', plus assetId/filters)
  const [scada, setScada] = useState(null);
  // Full alert list for the AlertsRail (left panel) — independent of the map overlay
  const [allAlerts, setAllAlerts] = useState([]);
  // Subset of alerts shown as pulsing markers on the map.
  // Empty by default per PM feedback (#4) — populated when a query enables them.
  const [mapAlertOverlay, setMapAlertOverlay] = useState([]);

  // Most-recent query meta for the Explain panel. Only present if the user has
  // executed at least one classified query.
  const [explainData, setExplainData] = useState(null);
  const [explainOpen, setExplainOpen] = useState(false);

  // Imperative API exposed by Map.jsx for the toolbar (basemap, draw, import).
  const mapApiRef = useRef(null);

  // Reactive role — re-renders the whole tree on role switch so RBAC changes are visible.
  const currentRole = useCurrentRole();

  // Load alerts on Nexus tenants for the AlertsRail. Map stays clean until a
  // query asks for spatial context.
  useEffect(() => {
    if (!isNexusTenant) return;
    const backend = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${backend}/api/scada/alerts?limit=400`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(data => setAllAlerts(data.alerts || []))
      .catch(err => console.warn('Alerts preload failed:', err.message));
  }, []);

  // Ref to ChatInterface for sending queries programmatically
  const chatRef = useRef(null);

  // Custom symbology overrides (user-driven color changes)
  const [symbologyOverrides, setSymbologyOverrides] = useState({
    tractTypes: {},   // e.g., { 'F': '#ff0000' }
    wellStatuses: {}  // e.g., { 'PRODUCING': '#00ff00' }
  });

  // Manual layer toggle from the Layers panel — fetches geojson for that layer
  // and sets queryResults to a synthetic multi-layer payload combining current + new.
  const handleLayerToggle = useCallback(async (layerKey, turnOn) => {
    if (!turnOn) {
      // Remove this layer from the current results
      setQueryResults((prev) => {
        if (!prev) return null;
        const filtered = (prev.features || []).filter((f) => f._sourceLayer !== layerKey);
        const layers = (prev.layers || []).filter((l) => l !== layerKey);
        if (filtered.length === 0) return null;
        return { ...prev, features: filtered, count: filtered.length, layers, layer: layers.length > 1 ? 'multi' : layers[0] };
      });
      const role = getCurrentRole();
      logAudit({ user: role.user, role: role.id, action: 'LAYER_TOGGLE', detail: `${layerKey}: off`, sources: ['GIS Server'] });
      return;
    }
    try {
      const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
      const r = await fetch(`${BACKEND}/api/geojson/${layerKey}`);
      if (!r.ok) throw new Error(`fetch /api/geojson/${layerKey} → ${r.status}`);
      const fc = await r.json();
      const tagged = (fc.features || []).map((f) => ({ ...f, _sourceLayer: layerKey }));
      setQueryResults((prev) => {
        const prevFeatures = (prev?.features || []).filter((f) => f._sourceLayer !== layerKey);
        const layersSet = new Set([...((prev?.layers) || []), layerKey]);
        if (prev?.layer && prev.layer !== 'multi') layersSet.add(prev.layer);
        const layers = Array.from(layersSet);
        const combined = [...prevFeatures, ...tagged];
        return {
          features: combined,
          count: combined.length,
          layer: layers.length > 1 ? 'multi' : layerKey,
          layers,
          zoom: true,
          naturalQuery: `Manually enabled ${layerKey} layer`,
          intent: layerKey,
          conditions: []
        };
      });
      const role = getCurrentRole();
      logAudit({ user: role.user, role: role.id, action: 'LAYER_TOGGLE', detail: `${layerKey}: on (${tagged.length} features)`, sources: ['GIS Server'], resultCount: tagged.length });
    } catch (e) {
      console.error(`handleLayerToggle ${layerKey} failed:`, e);
      setError(`Failed to load layer ${layerKey}: ${e.message}`);
    }
  }, []);

  // Handle query results from chat interface
  const handleQueryResults = useCallback((results) => {
    setQueryResults(results);
    setError(null);
    if (results) {
      const role = getCurrentRole();
      // Audit
      const sources = [];
      // Source attribution per intent — what data systems actually contributed to this result.
      // Always include the GIS server (everything spatial routes through it).
      if (results.layer) sources.push('GIS Server (ArcGIS)');
      if (results.analyticsData || results.analytics) sources.push('Databricks');

      const lay = results.layer === 'multi' ? (results.intent || '') : (results.layer || results.intent || '');
      const layerKey = typeof lay === 'string' ? lay : '';
      const layersArr = results.layers || [];

      // Per-intent source attribution
      if (layerKey === 'pipe_risk' || layersArr.includes('pipe_risk')) {
        sources.push('Survey reports (Q3-2025, Q2-2026, Q3-2026)');
        sources.push('PostgreSQL · pipe_history');
      }
      if (layerKey === 'leaks' || layersArr.includes('leaks')) {
        sources.push('Picarro CRDS survey telemetry');
      }
      if (results.proximity_to || layersArr.includes('sensitive_premises') || layerKey === 'sensitive_premises') {
        sources.push('Sensitive premises registry (49 CFR §192.624)');
      }
      if (layerKey === 'fenceline' || layerKey === 'sensors' || layerKey === 'dispersion_plumes' || layersArr.includes('sensors')) {
        sources.push('SCADA telemetry · fenceline sensors');
        sources.push('NOAA wind data');
      }
      if (layerKey === 'crews' || layerKey === 'work_orders_open' || layersArr.includes('crews')) {
        sources.push('SharePoint · Crew Dispatch Schedule');
        sources.push('PostgreSQL · work_orders');
      }
      if (layerKey === 'distribution_mains' || layerKey === 'transmission_mains' || layerKey === 'service_lines') {
        sources.push('GIS pipe inventory');
      }
      if (layerKey === 'survey_routes') {
        sources.push('OneDrive · Q1-2026 / Q2-2026 / Q3-2026 inspection reports');
      }
      if (layerKey === 'scada') {
        sources.push('SCADA event log');
      }
      // Deduplicate while preserving order
      const seen = new Set();
      const dedupedSources = sources.filter((s) => seen.has(s) ? false : (seen.add(s), true));
      sources.length = 0;
      sources.push(...dedupedSources);
      logAudit({
        user: role.user,
        role: role.id,
        action: 'QUERY',
        detail: results.query || results.naturalQuery || `Query on ${results.layer}`,
        sources,
        resultCount: (results.features && results.features.length) || results.count || 0
      });
      // Capture meta for the Explain panel
      // Count primary entity (leaks/crews/pipes/etc), not bundled supporting features (premises).
      const leakCount = (results.features || []).filter(f =>
        (f._sourceLayer === 'leaks') || (f._sourceLayer === undefined && f.properties?.grade !== undefined)
      ).length;
      const premiseCount = (results.features || []).filter(f => f._sourceLayer === 'sensitive_premises').length;
      const isProximity = !!results.proximity_to || (premiseCount > 0 && leakCount > 0);
      const primaryCount = isProximity ? leakCount : (results.count ?? (results.features?.length) ?? 0);
      setExplainData({
        naturalQuery: results.naturalQuery || results.query,
        intent: results.intent || results.layer,
        layer: results.layer,
        conditions: results.conditions || [],
        sources,
        resultCount: primaryCount,
        // Story-context extras the panel uses for the plain-English summary
        isProximity,
        leakCount,
        premiseCount,
        withinFt: results.within_ft,
        premiseType: results.premise_type,
        groupBy: results.groupBy,
        timestamp: new Date().toISOString(),
        fallbackUsed: !!results.fallbackUsed,
        classifier: results.fallbackUsed ? 'Regex fallback' : 'Claude (Anthropic)'
      });
    }
  }, []);

  // Handle SCADA-routed queries from ChatInterface.
  // payload: { mode: 'asset'|'region', assetId, filters, alertedAssetIds: [] }
  const handleScadaQuery = useCallback((payload) => {
    setScada({
      mode: payload.mode || 'region',
      assetId: payload.assetId || null,
      filters: payload.filters || {},
    });
    // Populate the map overlay with the alerts this query is about. Filter the
    // full alert list by the query's filters so the map highlights matching
    // alerts only — addresses PM feedback #4 (no all-on overlay) and #5/#7
    // (queries should drive what's shown).
    const f = payload.filters || {};
    let matching = allAlerts;
    if (f.asset_id) {
      matching = matching.filter(a => a.asset_id === f.asset_id);
    } else {
      if (f.severity) matching = matching.filter(a => a.severity === f.severity);
      if (f.county)   matching = matching.filter(a => (a.county || '').toLowerCase().includes(f.county.toLowerCase()));
    }
    setMapAlertOverlay(matching);
    // Populate the Explain panel even for SCADA queries (PM #3.1: Explain wasn't appearing for all results).
    const conditions = [];
    if (f.severity)  conditions.push({ field: 'severity', operator: '=', value: f.severity });
    if (f.county)    conditions.push({ field: 'county',   operator: '=', value: f.county });
    if (f.asset_id)  conditions.push({ field: 'asset_id', operator: '=', value: f.asset_id });
    setExplainData({
      naturalQuery: payload.naturalQuery || payload.query || `SCADA ${payload.mode || 'region'} query`,
      intent: 'scada',
      layer: 'scada',
      conditions,
      sources: ['GIS Server', 'SCADA Operations'],
      resultCount: matching.length,
      timestamp: new Date().toISOString(),
      fallbackUsed: false,
      classifier: 'Claude (Anthropic)'
    });
  }, [allAlerts]);

  // Click on a map alert marker → switch SCADA tab to that asset's dossier
  const handleScadaAssetClick = useCallback((assetId) => {
    setScada({ mode: 'asset', assetId, filters: {} });
    const match = allAlerts.find(a => a.asset_id === assetId);
    if (match) setMapAlertOverlay([match]);
  }, [allAlerts]);

  // Handle analytics data
  const handleAnalytics = useCallback((data) => {
    console.log('App received analytics data:', data);
    setAnalyticsData(data);
  }, []);

  // Clear analytics data
  const handleClearAnalytics = useCallback(() => {
    setAnalyticsData(null);
  }, []);

  // Handle symbology change requests from chat
  const handleSymbologyChange = useCallback((change) => {
    console.log('🎨 Symbology change:', change);

    if (change.target === 'wellStatus') {
      setSymbologyOverrides(prev => ({
        ...prev,
        wellStatuses: {
          ...prev.wellStatuses,
          [change.value]: change.color
        }
      }));
    } else if (change.target === 'tractType') {
      setSymbologyOverrides(prev => ({
        ...prev,
        tractTypes: {
          ...prev.tractTypes,
          [change.value]: change.color
        }
      }));
    } else if (change.target === 'allWells') {
      // Apply color to all well statuses
      const allWellStatuses = ['PRODUCING', 'DUC', 'DRILLING', 'PERMITTED', 'COMPLETED', 'INACTIVE PRODUCER', 'SHUT-IN', 'P & A'];
      const newOverrides = {};
      allWellStatuses.forEach(status => {
        newOverrides[status] = change.color;
      });
      setSymbologyOverrides(prev => ({
        ...prev,
        wellStatuses: newOverrides
      }));
    } else if (change.target === 'allTracts') {
      // Apply color to all tract types
      const allTractTypes = ['F', 'O', 'N', 'U', 'Q', 'V'];
      const newOverrides = {};
      allTractTypes.forEach(type => {
        newOverrides[type] = change.color;
      });
      setSymbologyOverrides(prev => ({
        ...prev,
        tractTypes: newOverrides
      }));
    }
  }, []);

  // Handle analytics filter clicks (e.g., clicking on a county/operator in analytics table)
  const handleAnalyticsFilterClick = useCallback((filterType, value) => {
    console.log('📊 Analytics filter clicked:', filterType, value);

    // Build a natural language filter query based on the filter type
    let filterQuery = '';
    switch (filterType) {
      case 'county':
        filterQuery = `Filter to ${value} County only`;
        break;
      case 'operator':
        filterQuery = `Filter to operator ${value}`;
        break;
      case 'status':
        filterQuery = `Filter to ${value} status`;
        break;
      case 'formation':
        filterQuery = `Filter to ${value} formation`;
        break;
      default:
        filterQuery = `Filter by ${filterType}: ${value}`;
    }

    // Send the query to ChatInterface via ref
    if (chatRef.current) {
      chatRef.current.sendQuery(filterQuery);
    }
  }, []);

  // Handle errors
  const handleError = useCallback((err) => {
    setError(err.message);
    console.error('Application error:', err);
  }, []);

  // Handle feature click on map - MUST be memoized to prevent map remount
  const handleFeatureClick = useCallback((feature, layerKey) => {
    setSelectedFeature({ feature, layerKey });
    setFeatureContracts([]); // Reset contracts when new feature selected
  }, []);

  // Fetch contracts when a feature is selected
  useEffect(() => {
    async function fetchFeatureContracts() {
      if (!selectedFeature) {
        setFeatureContracts([]);
        return;
      }

      console.log('🔍 Selected feature:', selectedFeature);
      console.log('🔍 Feature properties:', selectedFeature.feature.properties);
      console.log('🔍 Feature attributes:', selectedFeature.feature.attributes);

      // Check both properties and attributes (ArcGIS uses attributes)
      const props = selectedFeature.feature.properties || selectedFeature.feature.attributes || {};

      const tractNumber = props.TRACT_NUMBER ||
                         props.TractNumber ||
                         props.tract_number;

      console.log('🔍 Extracted tract number:', tractNumber);

      if (tractNumber) {
        try {
          console.log('🔍 Fetching contracts for tract:', tractNumber);
          const contracts = await getContractsByTractNumbers([tractNumber]);
          console.log('✅ Contracts fetched:', contracts);
          setFeatureContracts(contracts);
        } catch (error) {
          console.error('❌ Failed to fetch contracts for feature:', error);
          setFeatureContracts([]);
        }
      } else {
        console.warn('⚠️ No tract number found in feature properties');
        setFeatureContracts([]);
      }
    }

    fetchFeatureContracts();
  }, [selectedFeature]);

  // Drag handlers for feature panel
  const handleMouseDown = useCallback((e) => {
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - panelPosition.x,
      y: e.clientY - panelPosition.y
    });
  }, [panelPosition]);

  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      setPanelPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y
      });
    }
  }, [isDragging, dragOffset]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Click an alert in the left rail → open dossier
  const handleAlertRailClick = useCallback((alert) => {
    setScada({ mode: 'asset', assetId: alert.asset_id, filters: {} });
    setMapAlertOverlay([alert]);
  }, []);

  // ─── Nexus tenants (water + emissions): operations-console layout ───────
  if (isNexusTenant) {
    // RBAC: readonly users see fewer alerts (drop the operationally sensitive ones)
    const visibleAlerts = currentRole.id === 'readonly'
      ? allAlerts.filter(a => a.severity !== 'critical' || (a.asset_type !== 'crew' && a.asset_type !== 'pipe'))
      : allAlerts;
    return (
      <>
        <Layout
          brand={APP_TITLE}
          subtitle={APP_SUBTITLE}
          alerts={visibleAlerts}
          leftRail={
            <AlertsRail
              alerts={visibleAlerts}
              onAlertClick={handleAlertRailClick}
            />
          }
          map={
            <div style={{ position: 'relative', width: '100%', height: '100%' }}>
              <Map
                queryResults={queryResults}
                onFeatureClick={handleFeatureClick}
                symbologyOverrides={symbologyOverrides}
                scadaAlerts={mapAlertOverlay}
                onScadaAssetClick={handleScadaAssetClick}
                mapApiRef={mapApiRef}
                onLayerToggle={handleLayerToggle}
              />
              <MapToolbar mapApi={mapApiRef.current ? mapApiRef.current : { setBasemap: () => {}, getCurrentFeatures: () => [] }} />
              {explainData && (
                <button
                  onClick={() => setExplainOpen(true)}
                  style={{
                    position: 'absolute', bottom: 16, right: 16, zIndex: 1200,
                    background: '#2563eb', border: 'none', color: 'white',
                    borderRadius: 999, padding: '10px 18px', fontSize: 13, fontWeight: 600,
                    cursor: 'pointer', boxShadow: '0 6px 18px rgba(37,99,235,0.45)',
                    display: 'flex', alignItems: 'center', gap: 6
                  }}
                  title="See how the AI reached this result"
                >
                  <span>✨</span>
                  <span>Explain this result</span>
                </button>
              )}
            </div>
          }
          workspace={
            <WorkspaceTabs
              chat={
                <ChatInterface
                  ref={chatRef}
                  onQueryResults={handleQueryResults}
                  onAnalytics={handleAnalytics}
                  onClearAnalytics={handleClearAnalytics}
                  onError={handleError}
                  onSymbologyChange={handleSymbologyChange}
                  onScadaQuery={handleScadaQuery}
                />
              }
              dossier={scada && (
                <SCADAPanel
                  mode={scada.mode}
                  assetId={scada.assetId}
                  filters={scada.filters}
                />
              )}
              analytics={analyticsData && (
                <AnalyticsPanel
                  analyticsData={analyticsData}
                  onFilterClick={handleAnalyticsFilterClick}
                />
              )}
              databricks={
                <DatabricksPanel
                  isOpen
                  onToggle={() => {}}
                  selectedWell={selectedFeature}
                  analyticsData={analyticsData}
                  queryResults={queryResults}
                />
              }
              audit={<AuditPanel />}
              onboarding={canDo('onboard', currentRole.id) ? <OnboardingPanel /> : null}
              hasDossier={!!scada}
              hasAnalytics={!!analyticsData}
            />
          }
        />
        <RoleToast />
        <AnnotationModal />
        {explainOpen && <ExplainPanel explainData={explainData} onClose={() => setExplainOpen(false)} />}
      </>
    );
  }

  // ─── Non-water tenants: keep the existing TPL layout ────────────────────
  return (
    <div className="app">
      <header className="app-header">
        <div className="header-content">
          <img src="/tpl-icon.png" alt="Texas Pacific Land" className="tpl-logo" />
          <div className="header-text">
            <h1>{APP_TITLE}</h1>
            <p>{APP_SUBTITLE}</p>
          </div>
        </div>
      </header>

      <div className="app-content">
        <div className="map-section">
          <Map
            queryResults={queryResults}
            onFeatureClick={handleFeatureClick}
            symbologyOverrides={symbologyOverrides}
          />
          <AnalyticsPanel
            analyticsData={analyticsData}
            onFilterClick={handleAnalyticsFilterClick}
          />
        </div>

        <div className="chat-section">
          <ChatInterface
            ref={chatRef}
            onQueryResults={handleQueryResults}
            onAnalytics={handleAnalytics}
            onClearAnalytics={handleClearAnalytics}
            onError={handleError}
            onSymbologyChange={handleSymbologyChange}
          />
        </div>
      </div>

      {/* Error notification */}
      {error && (
        <div className="error-notification">
          <strong>Error:</strong> {error}
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* Selected feature panel */}
      {selectedFeature && (
        <div
          className="feature-panel"
          style={{
            left: `${panelPosition.x}px`,
            top: `${panelPosition.y}px`,
            cursor: isDragging ? 'grabbing' : 'auto'
          }}
        >
          <div
            className="feature-panel-header"
            onMouseDown={handleMouseDown}
            style={{ cursor: 'grab' }}
          >
            <h3>Tract Details</h3>
            <button onClick={() => setSelectedFeature(null)}>✕</button>
          </div>
          <div className="feature-panel-content">
            <p><strong>Layer:</strong> {selectedFeature.layerKey}</p>
            <hr />
            {(() => {
              const props = selectedFeature.feature.properties || selectedFeature.feature.attributes || {};
              return Object.entries(props).map(([key, value]) => (
                <p key={key}>
                  <strong>{key}:</strong> {value?.toString() || 'N/A'}
                </p>
              ));
            })()}

            {/* Contract Data Section */}
            {featureContracts.length > 0 && (
              <>
                <hr style={{ margin: '1rem 0', borderColor: '#4caf50' }} />
                <h4 style={{ color: '#4caf50', marginBottom: '0.5rem' }}>
                  📋 Contracts ({featureContracts.length})
                </h4>
                {featureContracts.map((contract, idx) => (
                  <div key={idx} style={{
                    background: '#e8f5e9',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem'
                  }}>
                    <p><strong>Contract ID:</strong> {contract.contract_id}</p>
                    <p><strong>Operator:</strong> {contract.operator_name}</p>
                    <p><strong>Type:</strong> {contract.contract_type}</p>
                    <p><strong>Status:</strong> {contract.lease_status}</p>
                    <p><strong>Start Date:</strong> {contract.lease_start_date}</p>
                    <p><strong>End Date:</strong> {contract.lease_end_date}</p>
                    <p><strong>Monthly Revenue:</strong> ${contract.monthly_revenue?.toLocaleString() || 0}</p>
                    <p><strong>Royalty %:</strong> {(contract.royalty_percentage * 100).toFixed(1)}%</p>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {/* Databricks panel — non-water tenants only.
          Water tenant uses the bottom InsightsTabs (Analytics + SCADA) instead so we
          don't have a competing floating button overlapping the tab bar. */}
      {!isWaterTenant && (
        <DatabricksPanel
          isOpen={databricksPanelOpen}
          onToggle={() => setDatabricksPanelOpen(!databricksPanelOpen)}
          selectedWell={selectedFeature}
          analyticsData={analyticsData}
          queryResults={queryResults}
        />
      )}

    </div>
  );
}

export default App;
